var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/**
 * Dedicated API client + hooks for the standalone voice-provider ADMIN console.
 *
 * This is intentionally SEPARATE from the shared `@/hoc/api/api-service` client
 * (which carries the normal user/IAM Bearer token). The admin console is gated
 * by its own static login (admin@vriti.ai), and its endpoints authenticate via
 * an `X-Voice-Admin-Token` header — issued by POST /admin/voice/login/ and
 * verified server-side with a fixed secret, so it works on every environment.
 *
 * Nothing here touches the shared client; the public scheduling calls
 * (voice/providers/ list) keep using api-service as before.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ADMIN_ROLES } from './admin-access';
const TOKEN_KEY = 'voice_admin_token';
const EXP_KEY = 'voice_admin_token_exp';
const EMAIL_KEY = 'voice_admin_email';
const ROLE_KEY = 'voice_admin_role';
const HEADER = 'X-Voice-Admin-Token';
// Base URL (e.g. http://host/api) — already includes `/api`, mirroring the
// shared client's convention. Set once by the login gate from config.apiBaseUrl.
let baseUrl = '';
export const setVoiceAdminBaseUrl = (url) => {
    baseUrl = url || '';
};
export const getVoiceAdminToken = () => localStorage.getItem(TOKEN_KEY);
/** The signed-in admin's email (for the console header). Empty until login. */
export const getVoiceAdminEmail = () => localStorage.getItem(EMAIL_KEY) || '';
/** The signed-in admin's role (VA-29489). Anything unrecognised gets the
 *  narrowest view; the session check on load stores the real one. */
export const getVoiceAdminRole = () => {
    const role = localStorage.getItem(ROLE_KEY);
    return ADMIN_ROLES.includes(role) ? role : 'blog_admin';
};
const storeRole = (role) => {
    if (typeof role === 'string')
        localStorage.setItem(ROLE_KEY, role);
};
export const isVoiceAdminAuthed = () => {
    const token = getVoiceAdminToken();
    const exp = Number(localStorage.getItem(EXP_KEY) || 0);
    return !!token && exp * 1000 > Date.now();
};
export const clearVoiceAdmin = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXP_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(ROLE_KEY);
};
const authHeaders = (json = true) => {
    // A FormData body sets its own multipart Content-Type (with the boundary).
    const headers = json ? { 'Content-Type': 'application/json' } : {};
    const token = getVoiceAdminToken();
    if (token)
        headers[HEADER] = token;
    return headers;
};
function request(method, path, body) {
    return __awaiter(this, void 0, void 0, function* () {
        const form = typeof FormData !== 'undefined' && body instanceof FormData;
        const res = yield fetch(`${baseUrl}${path}`, {
            method,
            headers: authHeaders(!form),
            body: form ? body : body !== undefined ? JSON.stringify(body) : undefined,
        });
        if (res.status === 401) {
            clearVoiceAdmin(); // token expired/invalid → force re-login
            // Let listeners (the gate) react to the storage change.
            try {
                window.dispatchEvent(new Event('voice-admin-unauthorized'));
            }
            catch (_a) {
                /* ignore */
            }
            throw { status: 401, error: 'Session expired — please log in again.' };
        }
        const data = yield res.json().catch(() => null);
        if (!res.ok) {
            // httpStatus lets callers tell a 409 "in use" from a 404 "not deployed".
            throw data && typeof data === 'object'
                ? Object.assign(Object.assign({}, data), { httpStatus: res.status }) : { error: `Request failed (${res.status})`, httpStatus: res.status };
        }
        return data;
    });
}
export const voiceAdminClient = {
    get: (path) => request('GET', path),
    post: (path, body) => request('POST', path, body),
    put: (path, body) => request('PUT', path, body),
    patch: (path, body) => request('PATCH', path, body),
    delete: (path, body) => request('DELETE', path, body),
};
export function voiceAdminLogin(email, password) {
    return __awaiter(this, void 0, void 0, function* () {
        const res = yield fetch(`${baseUrl}/admin/voice/login/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
        });
        const data = yield res.json().catch(() => null);
        if (!res.ok || !(data === null || data === void 0 ? void 0 : data.token)) {
            throw data && typeof data === 'object' ? data : { error: 'Login failed' };
        }
        localStorage.setItem(TOKEN_KEY, data.token);
        localStorage.setItem(EXP_KEY, String(data.expires_at));
        if (data.email)
            localStorage.setItem(EMAIL_KEY, String(data.email));
        storeRole(data.role);
        return data;
    });
}
/** Verify the stored token is still valid server-side. */
export function voiceAdminCheckSession() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!isVoiceAdminAuthed())
            return false;
        try {
            const session = yield voiceAdminClient.get('/admin/voice/session/');
            storeRole(session === null || session === void 0 ? void 0 : session.role);
            return true;
        }
        catch (_a) {
            return false;
        }
    });
}
// ── query keys ──────────────────────────────────────────────────────────────
const adminKeys = {
    providers: ['voice-admin', 'providers'],
    proctoringConfig: ['voice-admin', 'proctoring-config'],
    vadSettings: ['voice-admin', 'vad-settings'],
    recordingConfig: ['voice-admin', 'recording-config'],
    metering: (path, params) => ['voice-admin', 'metering', path, params !== null && params !== void 0 ? params : {}],
    priceBooks: ['voice-admin', 'metering', 'price-books'],
};
export const useAdminVoiceProviders = (enabled = true) => useQuery({
    queryKey: adminKeys.providers,
    queryFn: () => voiceAdminClient.get('/admin/voice/providers/'),
    enabled,
    refetchOnWindowFocus: false,
});
/** One provider's complete record — powers the detail view and the edit form. */
export const useAdminVoiceProvider = (providerId) => useQuery({
    enabled: !!providerId,
    queryKey: [...adminKeys.providers, providerId],
    queryFn: () => voiceAdminClient.get(`/admin/voice/providers/${providerId}/`),
    refetchOnWindowFocus: false,
});
/** Every provider in full — used by whole-provider export. */
export const fetchAllProvidersFull = () => voiceAdminClient.get('/admin/voice/providers/?full=true');
export const useCreateVoiceProvider = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (data) => voiceAdminClient.post('/admin/voice/providers/', data),
        onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.providers }),
    });
};
export const useUpdateVoiceProvider = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ providerId, data }) => voiceAdminClient.put(`/admin/voice/providers/${providerId}/`, data),
        onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.providers }),
    });
};
export const useDeleteVoiceProvider = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (providerId) => voiceAdminClient.delete(`/admin/voice/providers/${providerId}/`),
        onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.providers }),
    });
};
export const useVerifyVoiceProviderLeg = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ providerId, leg, ok, error, }) => 
        // POST, not PUT: confirming a leg is a state transition the server records,
        // and POST is the only method the endpoint implements (PUT answered 405, so
        // "Sounds correct" / "Transcript correct" never actually verified anything).
        voiceAdminClient.post(`/admin/voice/providers/${providerId}/verify/`, {
            leg,
            ok,
            error,
        }),
        onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.providers }),
    });
};
export const useProctoringConfigAdmin = (enabled = true) => useQuery({
    queryKey: adminKeys.proctoringConfig,
    queryFn: () => voiceAdminClient.get('/admin/proctoring/config/'),
    enabled,
    refetchOnWindowFocus: false,
});
export const useUpdateProctoringConfig = () => {
    const qc = useQueryClient();
    return useMutation({
        // Signature matches the previous api-service hook so callers are unchanged.
        // VA-28160: every field optional, because the endpoint takes PARTIAL
        // updates and the calibration bench saves one slider at a time. Sending
        // values the caller never touched is how a stale tab reverts someone
        // else's change.
        mutationFn: ({ captureWidth, headDeviationDeg, gazeDeviation, dwellSeconds, }) => voiceAdminClient.put('/admin/proctoring/config/', Object.assign(Object.assign(Object.assign(Object.assign({}, (captureWidth != null ? { capture_width: captureWidth } : {})), (headDeviationDeg != null ? { head_deviation_deg: headDeviationDeg } : {})), (gazeDeviation != null ? { gaze_deviation: gazeDeviation } : {})), (dwellSeconds != null ? { dwell_seconds: dwellSeconds } : {}))),
        onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.proctoringConfig }),
    });
};
export const useInterviewModeSettings = (enabled = true) => useQuery({
    queryKey: ['voice-admin', 'interview-mode'],
    queryFn: () => voiceAdminClient.get('/admin/voice/interview-mode/'),
    enabled,
    refetchOnWindowFocus: false,
});
export const useSaveInterviewModeSettings = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (realtimeEnabled) => voiceAdminClient.put('/admin/voice/interview-mode/', {
            realtime_enabled: realtimeEnabled,
        }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['voice-admin', 'interview-mode'] }),
    });
};
export const useVadSettings = (enabled = true) => useQuery({
    queryKey: adminKeys.vadSettings,
    queryFn: () => voiceAdminClient.get('/admin/voice/vad-settings/'),
    enabled,
    refetchOnWindowFocus: false,
});
export const useSaveVadSettings = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (data) => voiceAdminClient.put('/admin/voice/vad-settings/', data),
        onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.vadSettings }),
    });
};
export const useRecordingConfigAdmin = (enabled = true) => useQuery({
    queryKey: adminKeys.recordingConfig,
    queryFn: () => voiceAdminClient.get('/admin/recording/config/'),
    enabled,
    refetchOnWindowFocus: false,
});
export const useUpdateRecordingConfig = () => {
    const qc = useQueryClient();
    return useMutation({
        // Partial save: only the keys the operator actually changed are sent, and
        // the backend leaves absent ones alone.
        mutationFn: (patch) => voiceAdminClient.put('/admin/recording/config/', patch),
        onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.recordingConfig }),
    });
};
// ── token usage & cost metering (admin) ──────────────────────────────────────
// Read-only apart from the price-book endpoints and reprice. All of these report
// across EVERY org, which is exactly why they sit behind the voice-admin token
// rather than the normal IAM auth.
/** Drop empty params so a query key stays stable and the URL stays readable. */
const qs = (params) => {
    const entries = Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '');
    if (!entries.length)
        return '';
    return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
};
export const useMeteringSummary = (params, enabled = true) => useQuery({
    queryKey: adminKeys.metering('summary', params),
    queryFn: () => voiceAdminClient.get(`/admin/metering/summary/${qs(params)}`),
    enabled,
    refetchOnWindowFocus: false,
});
export const useMeteringOrgs = (params, enabled = true) => useQuery({
    queryKey: adminKeys.metering('orgs', params),
    queryFn: () => voiceAdminClient.get(`/admin/metering/orgs/${qs(params)}`),
    enabled,
    refetchOnWindowFocus: false,
});
export const useMeteringRecruiters = (params, enabled = true) => useQuery({
    queryKey: adminKeys.metering('recruiters', params),
    queryFn: () => voiceAdminClient.get(`/admin/metering/recruiters/${qs(params)}`),
    enabled,
    refetchOnWindowFocus: false,
});
export const useMeteringInterviews = (params, enabled = true) => useQuery({
    queryKey: adminKeys.metering('interviews', params),
    queryFn: () => voiceAdminClient.get(`/admin/metering/interviews/${qs(params)}`),
    enabled,
    refetchOnWindowFocus: false,
});
export const useMeteringChains = (params, enabled = true) => useQuery({
    queryKey: adminKeys.metering('chains', params),
    queryFn: () => voiceAdminClient.get(`/admin/metering/interviews/${qs(Object.assign(Object.assign({}, params), { group: 'chain' }))}`),
    enabled,
    refetchOnWindowFocus: false,
});
export const useMeteringRun = (slug) => useQuery({
    queryKey: adminKeys.metering('run', { slug: slug !== null && slug !== void 0 ? slug : undefined }),
    queryFn: () => voiceAdminClient.get(`/admin/metering/interviews/${encodeURIComponent(slug || '')}/`),
    enabled: !!slug,
    refetchOnWindowFocus: false,
});
export const useMeteringBreakdown = (params, enabled = true) => useQuery({
    queryKey: adminKeys.metering('breakdown', params),
    queryFn: () => voiceAdminClient.get(`/admin/metering/breakdown/${qs(params)}`),
    enabled,
    refetchOnWindowFocus: false,
});
export const useMeteringUnpriced = (params, enabled = true) => useQuery({
    queryKey: adminKeys.metering('unpriced', params),
    queryFn: () => voiceAdminClient.get(`/admin/metering/unpriced/${qs(params)}`),
    enabled,
    refetchOnWindowFocus: false,
});
export const useMeteringHealth = (enabled = true) => useQuery({
    queryKey: adminKeys.metering('health'),
    queryFn: () => voiceAdminClient.get('/admin/metering/health/'),
    enabled,
    refetchOnWindowFocus: false,
});
export const useMeteringPriceBooks = (withEntries = false, enabled = true) => useQuery({
    queryKey: adminKeys.metering('price-books', { entries: withEntries ? 'true' : undefined }),
    queryFn: () => voiceAdminClient.get(`/admin/metering/price-books/${qs({ entries: withEntries ? 'true' : undefined })}`),
    enabled,
    refetchOnWindowFocus: false,
});
export const useMeteringPriceBook = (version) => useQuery({
    queryKey: adminKeys.metering('price-book', { version: version !== null && version !== void 0 ? version : undefined }),
    queryFn: () => voiceAdminClient.get(`/admin/metering/price-books/${encodeURIComponent(version || '')}/`),
    enabled: !!version,
    refetchOnWindowFocus: false,
});
export const useActivatePriceBook = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ version, allowUnverified }) => voiceAdminClient.post(`/admin/metering/price-books/${encodeURIComponent(version)}/activate/`, { allow_unverified: !!allowUnverified }),
        // Activation changes which rates NEW usage is priced against, so every metering
        // read is stale afterwards — invalidate the whole namespace, not just the books.
        onSuccess: () => qc.invalidateQueries({ queryKey: ['voice-admin', 'metering'] }),
    });
};
export const useReprice = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => voiceAdminClient.post('/admin/metering/reprice/', body),
        onSuccess: (data) => {
            // A dry run wrote nothing, so nothing is stale.
            if (data === null || data === void 0 ? void 0 : data.applied)
                qc.invalidateQueries({ queryKey: ['voice-admin', 'metering'] });
        },
    });
};
const invalidateMetering = (qc) => qc.invalidateQueries({ queryKey: ['voice-admin', 'metering'] });
export const useCreatePriceBook = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => voiceAdminClient.post('/admin/metering/price-books/', body),
        onSuccess: () => invalidateMetering(qc),
    });
};
export const useClonePriceBook = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (_a) => {
            var { version } = _a, body = __rest(_a, ["version"]);
            return voiceAdminClient.post(`/admin/metering/price-books/${encodeURIComponent(version)}/clone/`, body);
        },
        onSuccess: () => invalidateMetering(qc),
    });
};
export const useDeletePriceBook = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ version, confirm }) => voiceAdminClient.delete(`/admin/metering/price-books/${encodeURIComponent(version)}/`, { confirm: !!confirm }),
        onSuccess: () => invalidateMetering(qc),
    });
};
export const useUpdatePriceBook = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (_a) => {
            var { version } = _a, body = __rest(_a, ["version"]);
            return voiceAdminClient.patch(`/admin/metering/price-books/${encodeURIComponent(version)}/`, body);
        },
        onSuccess: () => invalidateMetering(qc),
    });
};
export const useAddPriceEntry = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ version, entry }) => voiceAdminClient.post(`/admin/metering/price-books/${encodeURIComponent(version)}/entries/`, entry),
        onSuccess: () => invalidateMetering(qc),
    });
};
/**
 * Partial update. A field sent as `null` CLEARS that rate back to "unknown", which is
 * how an admin retracts a figure they can no longer source — so callers must send
 * only the fields they mean to change.
 */
export const useUpdatePriceEntry = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ version, modelKey, meter, patch, }) => voiceAdminClient.patch(`/admin/metering/price-books/${encodeURIComponent(version)}/entries/` +
            `${encodeURIComponent(modelKey)}/${encodeURIComponent(meter)}/`, patch),
        onSuccess: () => invalidateMetering(qc),
    });
};
export const useDeletePriceEntry = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ version, modelKey, meter, }) => voiceAdminClient.delete(`/admin/metering/price-books/${encodeURIComponent(version)}/entries/` +
            `${encodeURIComponent(modelKey)}/${encodeURIComponent(meter)}/`),
        onSuccess: () => invalidateMetering(qc),
    });
};
